import { useState, useEffect, useMemo } from "react";
import { loadFromSupabase } from "../../supabaseDB";

/* ============================================================================
 * ShipperProducts — read-only catalog of the products this shipper is
 * authorized to ship. Authorization comes from shipper_settings.
 * authorized_products (a list of product ids the admin selected on
 * Shipper Settings). If the admin hasn't configured anything yet, the
 * shipper sees an empty list with a "contact GFI" hint.
 * ============================================================================ */

const T = {
  en: {
    title: "Authorized Products",
    sub: "These are the products you are authorized to ship to Ground Fresh International. Contact GFI to request changes.",
    search: "Search by name or code…",
    columns: { code: "Code", description: "Description", category: "Category", size: "Size", units: "Units/Case", cases: "Cases/Pallet", label: "Label" },
    none: "—",
    empty: "No products authorized yet. Contact GFI.",
    noResults: "No products match your search.",
    category_og: "Organic", category_cv: "Conventional",
  },
  es: {
    title: "Productos Autorizados",
    sub: "Estos son los productos que estás autorizado a embarcar a Ground Fresh International. Contacta a GFI para solicitar cambios.",
    search: "Buscar por nombre o código…",
    columns: { code: "Código", description: "Descripción", category: "Categoría", size: "Tamaño", units: "Unidades/Caja", cases: "Cajas/Pallet", label: "Etiqueta" },
    none: "—",
    empty: "Aún no hay productos autorizados. Contacta a GFI.",
    noResults: "Ningún producto coincide con la búsqueda.",
    category_og: "Orgánico", category_cv: "Convencional",
  },
};

export default function ShipperProducts({ supplierId, lang }) {
  const t = T[lang === "en" ? "en" : "es"];
  const [allProducts, setAllProducts] = useState([]);
  const [authorized, setAuthorized] = useState([]);   /* array of product ids */
  const [search, setSearch] = useState("");
  /* FIX 45 — category filter + sortable headers. The filter state is
     "all" | "Organic" | "Conventional"; sortCol drives the table's
     ordering with an asc/desc toggle that mirrors the other admin
     registries. */
  const [catFilter, setCatFilter] = useState("all");
  const [sortCol, setSortCol] = useState("code");
  const [sortDir, setSortDir] = useState("asc");
  const [loading, setLoading] = useState(true);

  useEffect(function () {
    let alive = true;
    Promise.all([
      loadFromSupabase("products").catch(function () { return []; }),
      loadFromSupabase("shipper_settings").catch(function () { return []; }),
    ]).then(function (res) {
      if (!alive) return;
      const prods = res[0] || [];
      const settings = (res[1] || []).find(function (s) { return String(s.id) === String(supplierId); });
      const allowed = (settings && settings.authorizedProducts) || [];
      setAllProducts(prods);
      setAuthorized(Array.isArray(allowed) ? allowed.map(String) : []);
      setLoading(false);
    });
    return function () { alive = false; };
  }, [supplierId]);

  const list = useMemo(function () {
    const allow = new Set(authorized);
    let filtered = allProducts.filter(function (p) {
      if (p.isActive === false) return false;
      if (allow.size === 0) return false;
      return allow.has(String(p.id));
    });
    if (search.trim()) {
      const q = search.toLowerCase();
      filtered = filtered.filter(function (p) {
        return (p.productCode || "").toLowerCase().includes(q)
          || (p.description || "").toLowerCase().includes(q)
          || (p.sku || "").toLowerCase().includes(q)
          || (p.product || "").toLowerCase().includes(q);
      });
    }
    if (catFilter !== "all") {
      filtered = filtered.filter(function (p) {
        var isOg = p.category === "Organic" || p.category === "OG";
        return catFilter === "Organic" ? isOg : !isOg;
      });
    }
    const keyOf = function (p) {
      switch (sortCol) {
        case "code":        return (p.productCode || p.sku || "").toLowerCase();
        case "description": return (p.description || p.product || "").toLowerCase();
        case "category":    return (p.category || "").toLowerCase();
        case "size":        return String(p.size || "");
        case "units":       return parseInt(p.unitsPerCase || p.unitsCase, 10) || 0;
        case "cases":       return parseInt(p.casesPerPallet || p.casesPallet, 10) || 0;
        case "label":       return (p.labelInitials || "").toLowerCase();
        default:            return "";
      }
    };
    filtered = filtered.slice().sort(function (a, b) {
      var ka = keyOf(a), kb = keyOf(b);
      if (ka < kb) return sortDir === "asc" ? -1 : 1;
      if (ka > kb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return filtered;
  }, [allProducts, authorized, search, catFilter, sortCol, sortDir]);

  const toggleSort = function (col) {
    if (sortCol === col) { setSortDir(sortDir === "asc" ? "desc" : "asc"); return; }
    setSortCol(col); setSortDir("asc");
  };
  const arrow = function (col) {
    if (sortCol !== col) return " ↕";
    return sortDir === "asc" ? " ↑" : " ↓";
  };

  const th = { textAlign: "left", padding: "10px 12px", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--color-text-secondary)", borderBottom: "2px solid var(--color-border)" };
  const td = { padding: "10px 12px", borderBottom: "1px solid var(--color-border)", fontSize: 13 };

  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>{t.title}</h2>
        <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--color-text-muted)" }}>{t.sub}</p>
      </div>

      <div style={{ marginBottom: 12, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <input type="text" value={search} onChange={function (e) { setSearch(e.target.value); }}
          placeholder={t.search}
          style={{ padding: "9px 14px", fontSize: 13, border: "1px solid var(--color-border)", borderRadius: 6, background: "var(--color-bg-input)", color: "var(--color-text)", outline: "none", fontFamily: "inherit", width: 320, maxWidth: "100%" }} />
        {/* FIX 45 — category filter. */}
        <select value={catFilter} onChange={function (e) { setCatFilter(e.target.value); }}
          style={{ padding: "9px 14px", fontSize: 13, border: "1px solid var(--color-border)", borderRadius: 6, background: "var(--color-bg-input)", color: "var(--color-text)", outline: "none", fontFamily: "inherit" }}>
          <option value="all">{lang === "es" ? "Todas las categorías" : "All categories"}</option>
          <option value="Organic">{t.category_og}</option>
          <option value="Conventional">{t.category_cv}</option>
        </select>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: "var(--color-text-muted)", fontSize: 13 }}>…</div>
      ) : list.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", color: "var(--color-text-muted)", fontSize: 13, fontStyle: "italic" }}>
          {authorized.length === 0 ? t.empty : t.noResults}
        </div>
      ) : (
        <div style={{ borderRadius: 10, border: "1px solid var(--color-border)", overflow: "hidden", background: "var(--color-bg-card)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {/* FIX 59 — label thumbnail (read-only mirror of the admin column). */}
                <th style={Object.assign({}, th, { width: 56, textAlign: "center", cursor: "default" })}></th>
                <th onClick={function () { toggleSort("code"); }} style={Object.assign({}, th, { cursor: "pointer" })}>{t.columns.code}{arrow("code")}</th>
                <th onClick={function () { toggleSort("description"); }} style={Object.assign({}, th, { cursor: "pointer" })}>{t.columns.description}{arrow("description")}</th>
                <th onClick={function () { toggleSort("category"); }} style={Object.assign({}, th, { cursor: "pointer" })}>{t.columns.category}{arrow("category")}</th>
                <th onClick={function () { toggleSort("size"); }} style={Object.assign({}, th, { cursor: "pointer" })}>{t.columns.size}{arrow("size")}</th>
                <th onClick={function () { toggleSort("units"); }} style={Object.assign({}, th, { cursor: "pointer", textAlign: "right" })}>{t.columns.units}{arrow("units")}</th>
                {/* FIX 61 — Cases per Pallet column. */}
                <th onClick={function () { toggleSort("cases"); }} style={Object.assign({}, th, { cursor: "pointer", textAlign: "right" })}>{t.columns.cases}{arrow("cases")}</th>
                <th onClick={function () { toggleSort("label"); }} style={Object.assign({}, th, { cursor: "pointer" })}>{t.columns.label}{arrow("label")}</th>
              </tr>
            </thead>
            <tbody>
              {list.map(function (p) {
                const isOg = p.category === "Organic" || p.category === "OG";
                return (
                  <tr key={p.id}>
                    {/* FIX 59 — label thumbnail (mirrors admin ProductRegistry). */}
                    <td style={Object.assign({}, td, { padding: "6px 6px", textAlign: "center" })}>
                      {p.labelPhoto && p.labelPhoto !== "__stored__" ? (
                        <img src={p.labelPhoto} alt="" style={{ width: 36, height: 36, objectFit: "cover", borderRadius: 4, border: "1px solid var(--color-border)", background: "#fff" }} />
                      ) : (
                        <div style={{ width: 36, height: 36, borderRadius: 4, border: "1px dashed var(--color-border)", background: "var(--color-bg)", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "var(--color-text-muted)", fontSize: 16 }}>🏷</div>
                      )}
                    </td>
                    <td style={Object.assign({}, td, { fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-accent)" })}>{p.productCode || p.sku || "—"}</td>
                    <td style={Object.assign({}, td, { fontWeight: 500 })}>{p.description || p.product || "—"}</td>
                    <td style={td}>
                      <span style={{
                        padding: "2px 8px", borderRadius: 8, fontSize: 10, fontWeight: 700,
                        background: isOg ? "var(--color-badge-og-bg)" : "var(--color-badge-cv-bg)",
                        color: isOg ? "var(--color-badge-og-text)" : "var(--color-badge-cv-text)",
                        border: "1px solid " + (isOg ? "var(--color-badge-og-border)" : "var(--color-badge-cv-border)"),
                      }}>{isOg ? t.category_og : t.category_cv}</span>
                    </td>
                    <td style={td}>{p.size || "—"}</td>
                    {/* FIX 60 — the real catalog field is `unitsPerCase`,
                        legacy records may carry `unitsCase`. */}
                    <td style={Object.assign({}, td, { fontFamily: "'JetBrains Mono', monospace", textAlign: "right" })}>{p.unitsPerCase || p.unitsCase || "—"}</td>
                    {/* FIX 61 — Cases per Pallet. */}
                    <td style={Object.assign({}, td, { fontFamily: "'JetBrains Mono', monospace", textAlign: "right" })}>{p.casesPerPallet || p.casesPallet || "—"}</td>
                    <td style={td}>{p.labelInitials || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
